<template>
  <VDialogV3
    id="tender-requisites"
    position="right"
    width="600px"
    :entity="localTender"
    @submit="submit"
  >
    <section>
      <VSelectV2
        v-model="localTender.winnersCategory"
        required
        :data="winnersCategorySource"
        :disabled="tender.canChangeRequisites === false"
      />
    </section>

    <hr>

    <section>
      <VSelectV2
        v-model="localTender.product"
        label="Товар"
        required
        disabled
      />

      <VInputV2
        v-model="localTender.product.code"
        label="Артикул"
        required
        disabled
      />

      <VSelectV2
        v-model="localTender.productCategory"
        label="Категория"
        required
        disabled
      />
    </section>

    <hr>

    <section>
      <VSelectV2
        v-model="localTender.buyerCompany"
        label="Юр. лицо"
        required
        :data="buyerCompanySource"
        :disabled="tender.canChangeRequisites === false"
      />

      <VSelectV2
        v-model="localTender.buyerStore"
        label="Склад"
        required
        :data="buyerStoreSource"
        :disabled="tender.canChangeRequisites === false"
      />

      <VSelectV2
        v-model="localTender.buyerProductionSite"
        label="Производственная площадка"
        :data="buyerProductionSiteSource"
        :disabled="tender.canChangeRequisites === false"
        validate-options
      />
    </section>

    <hr>

    <section>
      <VDateInput
        v-if="showDesiredReceiptDate === true"
        v-model="localTender.desiredReceiptDate"
        label="Дата поставки"
        layout="vertical"
        required
        :disabled="tender.canChangeRequisites === false"
        :validate-method="validateDesiredReceiptDate"
      />

      <VDateRangeInputNew
        v-if="showPreorderShipmentPeriod === true"
        v-model="localTender.preorderShipmentPeriod"
        label="Период поставки"
        layout="vertical"
        required
        :disabled="localTender.isQtyOrderedFlexible === false"
      />

      <VDateTimeInput
        v-model="localTender.startAt"
        date-label="Начало торгов"
        layout="vertical"
        required
        :disabled="tender.canChangeRequisites === false || localTender.isCreationDelayMode === true"
        :max-date="deliveryLimit"
      />

      <VCheckbox v-if="localTender.isExisting === false" v-model="localTender.isCreationDelayMode">
        Начать торги через {{ $core.utils.date.toMinutes(TendersService.config.CREATION_DELAY_MS) }} минут
      </VCheckbox>

      <VDateTimeInput
        v-model="localTender.endAt"
        date-label="Конец торгов"
        layout="vertical"
        required
        :disabled="tender.canChangeRequisites === false"
        :min-date="localTender.startAt"
        :max-date="deliveryLimit"
      />

      <VHelperText v-if="deliveryLimit" class="description" theme="blue-flat">
        Торги должны завершиться до {{ deliveryLimit }}
      </VHelperText>
    </section>

    <section>
      <VFormItem>
        <VInputNumberRusV2
          v-model="localTender.priceStartFull"
          :type="Price"
          label="Стартовая (максимальная) цена"
          layout="vertical"
          required
          :disabled="tender.canChangeRequisites === false"
          :validate-method="validatePriceStartFull"
        />

        <VSelectV2
          v-model="priceStartCurrency"
          label="Валюта"
          layout="vertical"
          required
          :data="currencySource"
          :disabled="tender.canChangeRequisites === false"
        />
      </VFormItem>
    </section>

    <section class="time-info">
      <table class="fill">
        <thead>
        <tr>
          <th class="empty"/>
          <th class="start">Начало</th>
          <th class="end">Конец</th>
        </tr>
        </thead>
        <tbody>
        <tr>
          <td class="nowrap">{{ timeZoneStringRepresentation }}</td>
          <td><span class="nowrap">{{ localTender.startAt }}</span></td>
          <td><span class="nowrap">{{ localTender.endAt || '--' }}</span></td>
        </tr>
        <tr>
          <td>МСК</td>
          <td><span class="nowrap">{{ localTender.startAt.toTimezone('Europe/Moscow') }}</span></td>
          <td><span class="nowrap">{{ localTender.endAt?.toTimezone('Europe/Moscow') || '--' }}</span></td>
        </tr>
        <tr>
          <td>UTC</td>
          <td><span class="nowrap">{{ localTender.startAt.toTimezone('UTC') }}</span></td>
          <td><span class="nowrap">{{ localTender.endAt?.toTimezone('UTC') || '--' }}</span></td>
        </tr>
        </tbody>
      </table>

      <dl class="start-price">
        <dt>Стартовая цена:</dt>
        <dd>{{ localTender.priceStartFull.format(priceStartCurrency, localTender.unit) }}</dd>
      </dl>
    </section>

    <hr>

    <section>
      <VSelectV2
        v-model="localTender.responsibleBuyer"
        label="Ответственный"
        placeholder="Поиск по ФИО"
        required
        clearable
        :data="responsibleBuyerSource"
        :disabled="tender.canChangeRequisites === false"
      />
    </section>
  </VDialogV3>
</template>

<style lang="scss" src="./TenderRequisites.scss"/>

<script lang="ts">
import PersonsService         from '@/domains/Persons/PersonsService';
import CompaniesService       from '@/domains/Companies/CompaniesService';
import StoresService          from '@/domains/Stores/StoresService';
import ProductionSitesService from '@/domains/ProductionSites/ProductionSitesService';
import TendersService         from '@/domains/Tenders/TendersService';
import Tender                 from '@/domains/Tenders/Tender';
import DateSimple             from '@/core/types/DateSimple';
import Price                  from '@/core/types/Price';

export default defineComponent({
  name: 'TenderRequisites',

  props: {
    /** Редактируемый тендер. Форма работает с его клоном. */
    tender: {
      type:     Object as PropType<Tender>,
      required: true,
    },
  },

  emits: ['updated'],

  setup() {
    return {
      TendersService,
      Price,
    };
  },

  data() {
    /**
     * Категория победителей: список фиксирован, дефолт — первая категория.
     * Раньше её проставлял Tender.fromDemand, теперь это забота источника.
     */
    const winnersCategorySource = this.$core.loader.select(TendersService.WINNERS_CATEGORIES.toArray().filter(item => item.id !== 0))

    const buyerCompanySource = this.$core.loader.select<Tender>(({ buyer }) =>
      CompaniesService.getList({ partnerId: buyer.id })
    )
      .dependsOn({ buyer: () => this.localTender.buyer })
      .default((options) => options.find(_option => _option.id === 66))
      .preloadOnMount(this.tender.isExisting === false);

    const buyerStoreSource = this.$core.loader.select<Tender>(({ buyer }) =>
      StoresService.getList({ partnerId: buyer.id })
    )
      .dependsOn({ buyer: () => this.localTender.buyer })
      .default((options) => options.find(_option => _option.id === 47))
      .preloadOnMount(this.tender.isExisting === false)
      .preloadOnFocus();

    const buyerProductionSiteSource = this.$core.loader.select<Tender>(({ buyerCompany }) =>
      ProductionSitesService.getList(null, buyerCompany)
    )
      .dependsOn({ buyerCompany: () => this.localTender.buyerCompany })
      .default((options) => options.find(_option => _option.id === 'berdsk'))
      .preloadOnMount(this.tender.isExisting === false)
      .preloadOnFocus();

    const responsibleBuyerSource = this.$core.loader.select(({ pattern }) =>
      PersonsService.searchPersons(pattern, this.$core.currentPartner.id)
    );

    /**
     * Валюта стартовой цены. Дефолт — та, в которой пользователь сейчас
     * смотрит платформу: тендер ещё не сохранён, другой валюты у него нет.
     */
    const currencySource = this.$core.loader.select(
      Array.from(this.$core.currencies.currencies.values())
    )
      .setIdentity('code')
      .default((options) => options.find(_option => _option.code === this.$core.displayedCurrencyCode));

    return {
      winnersCategorySource,
      buyerCompanySource,
      buyerStoreSource,
      buyerProductionSiteSource,
      responsibleBuyerSource,
      currencySource,

      /** Клон: пользователь может отменить изменения, и они не утекут в оригинал. */
      localTender: this.$core.utils.cloneDeep(this.tender),
    };
  },

  computed: {
    /**
     * Момент, до которого должны пройти торги: у предзаказа и гибкого
     * количества — начало периода отгрузки, у обычного тендера — дата
     * поставки. Обе границы торгов упираются в него сверху.
     */
    deliveryLimit(): DateSimple | null {
      if (this.localTender.isPreorderNeeded === true || this.localTender.isQtyOrderedFlexible === true) {
        return this.localTender.preorderShipmentPeriod?.start ?? null;
      } else {
        return this.localTender.desiredReceiptDate ?? null;
      }
    },

    /** Пояс пользователя без префикса «UTC±HH:MM » — он и так виден в таблице. */
    timeZoneStringRepresentation(): string {
      return this.$core.timeZone.stringRepresentation.slice(12);
    },

    priceStartCurrency: {
      get() {
        return this.$core.utils.money.getCurrencyByCode(this.localTender.priceStartCurrencyCode);
      },
      set({ code }) {
        this.localTender.priceStartCurrencyCode = code;
      },
    },

    /**
     * Дата поставки — только для обычного тендера с фиксированным количеством.
     */
    showDesiredReceiptDate(): boolean {
      return this.localTender.isPreorderNeeded === false
        && this.localTender.isQtyOrderedFlexible === false;
    },

    /**
     * Период поставки — для предзаказа или гибкого количества.
     */
    showPreorderShipmentPeriod(): boolean {
      return this.localTender.isPreorderNeeded === true
        || this.localTender.isQtyOrderedFlexible === true;
    },
  },

  methods: {
    /**
     * Сохранение. Заполненный клон отдаётся наверх событием — вызывающий сам
     * решает, куда его положить: карточка подменяет свой тендер, карточка
     * создания — образец пачки. Отправкой на бэк при создании тоже занимается
     * вызывающая сторона: TenderCreateCard раскладывает общие поля по пачке
     * и шлёт запросы сам.
     */
    async submit(): Promise<void> {
      if (this.localTender.isExisting === true) {
        await TendersService.update(this.localTender);
      }

      this.$emit('updated', this.localTender);

      this.$core.dialogs.TenderRequisites.resolve();
    },

    validateDesiredReceiptDate(date): string | null {
      date = new DateSimple(date);

      if (date.isBefore(DateSimple.today()) === true) {
        return 'Дата не может быть раньше сегодняшнего дня';
      }

      return null;
    },

    validatePriceStartFull(value: Price): string | null {
      if (this.$core.utils.isNullOrUndefined(value) === true || value.isEmpty() === true) {
        return 'Некорректное значение';
      }

      if (value.isZero() === true) {
        return 'Цена не может быть 0';
      }

      return null;
    },
  },
});
</script>
